import {
  asBoolean,
  asDate,
  asNumber,
  asString,
  isObject,
} from "./model-utils";
import {
  BillingCycle,
  BillingMode,
  CartStyle,
  CommissionBearerType,
  CommissionType,
  enumValue,
  InvoiceLogoOption,
  MemberType,
  OrgType,
  PaymentPlan,
  StoreTemplate,
} from "./enums";

export class OrganisationSettings {
  [key: string]: any;
  constructor(json: any = {}) {
    Object.assign(this, json);
    this.type = enumValue(OrgType, json.type);
    this.paymentPlan = enumValue(PaymentPlan, json.paymentPlan);
    this.memberProfileLock = asBoolean(json.memberProfileLock);
    this.allowUserAccess = asBoolean(json.allowUserAccess);
    this.manageUserProfile = asBoolean(json.manageUserProfile);
    this.id = asString(json._id ?? json.id);
    this.displayMembersInPlan = asBoolean(json.displayMembersInPlan);
    this.allowUserJoinPlan = asBoolean(json.allowUserJoinPlan);
    this.globalProfile = asBoolean(json.globalProfile);
    this.enableDiscoverChannel = asBoolean(json.enableDiscoverChannel);
    this.enableIdentityCard = asBoolean(json.enableIdentityCard);
    this.viewActivityBasedOnMembership = asBoolean(
      json.viewActivityBasedOnMembership,
    );
    this.viewIdentityCardBasedOnMembership = asBoolean(
      json.viewIdentityCardBasedOnMembership,
    );
    this.oneTimeRegistrationFee = asBoolean(json.oneTimeRegistrationFee);
    this.viewActivityBasedOnRegistration = asBoolean(
      json.viewActivityBasedOnRegistration,
    );
    this.viewIdentityCardBasedOnRegistration = asBoolean(
      json.viewIdentityCardBasedOnRegistration,
    );
    this.registrationFee = asNumber(json.registrationFee);
    this.postThread = enumValue(MemberType, json.postThread);
    this.photoIdentityCardLock = asBoolean(json.photoIdentityCardLock);
    this.allowMembersToInvite = asBoolean(json.allowMembersToInvite);
    this.postPublicThread = enumValue(MemberType, json.postPublicThread);
    this.excludePublicFeedFromThread = asBoolean(
      json.excludePublicFeedFromThread,
    );
    this.enableMembership = asBoolean(json.enableMembership);
    this.enableGiftCash = asBoolean(json.enableGiftCash);
    this.enableEvent = asBoolean(json.enableEvent);
    this.enableGames = asBoolean(json.enableGames);
    this.enableFundMe = asBoolean(json.enableFundMe);
    this.allowDownload = asBoolean(json.allowDownload) ?? true;
    this.allowMembersToUpdateMemberNo = asBoolean(
      json.allowMembersToUpdateMemberNo,
    );
    this.commissionPercentage = asNumber(json.commissionPercentage);
    this.commissionType =
      enumValue(CommissionType, json.commissionType) ?? CommissionType.DEFAULT;
    this.transactionChargeBearer = enumValue(
      CommissionBearerType,
      json.transactionChargeBearer,
    );
    this.registrationGrace = asNumber(json.registrationGrace);
    this.paymentExtensionDays = asNumber(json.paymentExtensionDays);
    this.enableAccessControl = asBoolean(json.enableAccessControl) ?? false;
    this.canCreateAccessCode = enumValue(MemberType, json.canCreateAccessCode);
    this.canCreateChannel = enumValue(MemberType, json.canCreateChannel);
    this.enforceAccessCodeWithMembership =
      asBoolean(json.enforceAccessCodeWithMembership) ?? false;
    this.enforceAccessCodeWithPayment =
      asBoolean(json.enforceAccessCodeWithPayment) ?? false;
    this.enableDependant = asBoolean(json.enableDependant);
    this.memberGatePassAccessDuration = asNumber(
      json.memberGatePassAccessDuration,
    );
    this.allowPermanentAccessCode = asBoolean(json.allowPermanentAccessCode);
    this.invoiceDuration = asNumber(json.invoiceDuration);
    this.noOffAllowedLocation = asNumber(json.noOffAllowedLocation);
    this.invoiceVatPercentage = asNumber(json.invoiceVatPercentage);
    this.invoiceSignature = asBoolean(json.invoiceSignature);
    this.uniqueInvoicePerLocation = asBoolean(json.uniqueInvoicePerLocation);
    this.inventoryScopedProducts = asBoolean(json.inventoryScopedProducts);
    this.enableProductSubCategory = asBoolean(json.enableProductSubCategory);
    this.logisticsScopedProducts = asBoolean(json.logisticsScopedProducts);
    this.invoiceNote = asString(json.invoiceNote);
    this.invoiceSignatureUrl = asString(json.invoiceSignatureUrl);
    this.showProductImageInInvoice = asBoolean(json.showProductImageInInvoice);
    this.invoiceBusinessName = asString(json.invoiceBusinessName);
    this.invoiceLogoUrl = asString(json.invoiceLogoUrl);
    this.invoiceLogoOption = enumValue(
      InvoiceLogoOption,
      json.invoiceLogoOption,
    );
    this.enableMemberCheckInButton = asBoolean(json.enableMemberCheckInButton);
    this.enableOnlineStore = asBoolean(json.enableOnlineStore);
    this.storeName = asString(json.storeName);
    this.storeCartStyle = enumValue(CartStyle, json.storeCartStyle);
    this.storeTemplate = enumValue(StoreTemplate, json.storeTemplate);
    this.storePaymentMessage = asString(json.storePaymentMessage);
    this.pickUpLocation = asString(json.pickUpLocation);
    this.storeEditDurationInHours = asNumber(json.storeEditDurationInHours);
    this.inactivityDuration = asNumber(json.inactivityDuration);
    this.storefrontMarkUpPercentage = asNumber(json.storefrontMarkUpPercentage);
    this.subscriptionName = asString(json.subscriptionName);
    this.deactivateAdminAccount = asBoolean(json.deactivateAdminAccount);
    this.enforceRequestMessage = asBoolean(json.enforceRequestMessage);
    this.onlyEnforceBillingOnFullAdmin = asBoolean(
      json.onlyEnforceBillingOnFullAdmin,
    );
    this.disableBillingDueReminder = asBoolean(json.disableBillingDueReminder);
    this.disableStoreFrontOrderReview = asBoolean(
      json.disableStoreFrontOrderReview,
    );
    this.storefrontPublic = asBoolean(json.storefrontPublic);
    this.enablePoll = asBoolean(json.enablePoll);
    this.communityScoped = asBoolean(json.communityScoped);
    this.enableExecutiveChannelDm = asBoolean(json.enableExecutiveChannelDm);
    this.allowFreeAdmin = asBoolean(json.allowFreeAdmin);
    this.enforceBilling = asBoolean(json.enforceBilling);
    this.paymentDueDate = asDate(json.paymentDueDate);
    this.paymentExtensionDate = asDate(json.paymentExtensionDate);
    this.staffFocusMode = asBoolean(json.staffFocusMode);
    this.multiStaffDeviceAccess = asBoolean(json.multiStaffDeviceAccess);
    this.enforceStaffAccessPin = asBoolean(json.enforceStaffAccessPin);
    this.enableOffline = asBoolean(json.enableOffline);
    this.enableOfflineActivity = asBoolean(json.enableOfflineActivity);
    this.oneTimeStoreFrontMemberDiscount = asNumber(
      json.oneTimeStoreFrontMemberDiscount,
    );
    this.oneTImeStoreFrontDiscountStartDate = asDate(
      json.oneTImeStoreFrontDiscountStartDate,
    );
    this.commissionCapAmount = asNumber(json.commissionCapAmount);
    this.commissionCap = asBoolean(json.commissionCap);
    this.includeVATInBillingInvoice = asBoolean(json.includeVATInBillingInvoice);
    this.billingMode =
      enumValue(BillingMode, json.billingMode) ?? BillingMode.POST_PAID;
    this.billingCycle =
      enumValue(BillingCycle, json.billingCycle) ?? BillingCycle.CALENDAR;
    this.enableMarketPlace = asBoolean(json.enableMarketPlace);
    this.allowPaymentExtensionRequest = asBoolean(
      json.allowPaymentExtensionRequest,
    );
    this.allowMultiplePaymentExtensionRequest = asBoolean(
      json.allowMultiplePaymentExtensionRequest,
    );
    this.noOffPaymentExtensionRequest = asNumber(
      json.noOffPaymentExtensionRequest,
    );
    this.allowViewMemberProfile = asBoolean(json.allowViewMemberProfile);
    this.enableExternalDependantProfile = asBoolean(
      json.enableExternalDependantProfile,
    );
    this.maxRepeatCodeDuration = asNumber(json.maxRepeatCodeDuration);
    this.maximumDependant = asNumber(json.maximumDependant);
  }

  get isBasic() {
    return this.paymentPlan === PaymentPlan.BASIC;
  }

  get isFree() {
    return this.paymentPlan === PaymentPlan.FREE;
  }

  get isEnterprise() {
    return this.paymentPlan === PaymentPlan.ENTERPRISE;
  }

  get isPrivate() {
    return this.type === OrgType.PRIVATE;
  }

  get isPersonalCommission() {
    return this.commissionType === CommissionType.PERSONAL;
  }

  get isProductCartStyle() {
    return this.storeCartStyle === CartStyle.PRODUCT;
  }

  get isMarkUpPricePresent() {
    return (this.storefrontMarkUpPercentage ?? 0) > 0;
  }

  get storefrontPublicUse() {
    return Boolean(this.storefrontPublic && this.enableOnlineStore);
  }

  get memberRegDiscountPresent() {
    return (this.oneTimeStoreFrontMemberDiscount ?? 0) > 0;
  }

  static fromJson(json: any) {
    return isObject(json) ? new OrganisationSettings(json) : json;
  }
}
